import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../core/scroll-lock";
import { adminFetchSubsidiePartners, adminSetApplicationPartner } from "../core/axios/endpoints";

const fmtEuro = (n) =>
  typeof n === "number"
    ? n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "-";

// Portal-modal om een subsidie-aanvraag (alsnog) aan een partner te koppelen.
// De keuzelijst bevat alleen partners met de module van deze regeling; de
// server handhaaft dat ook. Na koppelen betaalt de klant niets meer en komt
// de aanvraag na Goedkeuring als regel op de partner-factuur.
const ApplicationPartnerModal = ({
  isOpen,
  application,
  onClose,
  onSaved,
  zIndex = 9999,
}) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");

  const regling = application?.regling ?? "";
  const currentPartnerId = application?.partnerUserId?._id ?? application?.partnerUserId ?? "";
  const isGoedgekeurd = application?.status === "Goedgekeurd";

  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [isOpen, busy, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError("");
    setSelected("");
    setLoading(true);
    adminFetchSubsidiePartners(regling)
      .then((res) => {
        if (cancelled) return;
        const docs = res?.docs ?? res?.data?.docs ?? [];
        setPartners(docs.filter((p) => String(p._id) !== String(currentPartnerId)));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || "Partners konden niet geladen worden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, regling, currentPartnerId]);

  if (!isOpen) return null;

  const chosen = partners.find((p) => String(p._id) === selected);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const updated = await adminSetApplicationPartner(application._id, selected);
      onSaved?.(updated, chosen);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Koppelen is mislukt. Probeer het opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="application-partner-modal-title"
      style={{ position: "fixed", inset: 0, zIndex }}
      className="flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-[#091E428A]"
        onClick={() => {
          if (!busy) onClose?.();
        }}
      />
      <div className="relative w-full max-w-lg rounded-md bg-white p-6 shadow-overlay">
        <h3 id="application-partner-modal-title" className="text-xl font-medium text-ink">
          {currentPartnerId ? "Andere partner kiezen" : "Koppel aan partner"}
        </h3>
        <p className="mt-2 text-sm text-ink-subtle">
          De aanvraag gaat dan in de partner-factuurflow: de klant betaalt niets en na
          goedkeuring komt de aanvraag als regel op de verzamelfactuur van de partner
          (no cure no pay). Alleen partners met de module <strong>{regling}</strong> staan in de lijst.
        </p>

        <label className="mt-5 block text-xs font-semibold text-ink-subtlest" htmlFor="application-partner-select">
          Partner
        </label>
        <select
          id="application-partner-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={loading || busy}
          className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none disabled:opacity-50"
        >
          <option value="">{loading ? "Laden..." : "Kies een partner"}</option>
          {partners.map((p) => (
            <option key={p._id} value={p._id}>
              {(p.bedrijfsnaam || p.naam || p.email) +
                (typeof p.priceExcl === "number" ? ` (€ ${fmtEuro(p.priceExcl)} excl. btw)` : "")}
            </option>
          ))}
        </select>
        {!loading && partners.length === 0 && !error ? (
          <p className="mt-2 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
            Geen partner heeft de module {regling} aan staan. Zet die eerst aan via Klanten.
          </p>
        ) : null}

        {chosen ? (
          <div className="mt-4 rounded bg-sunken px-3 py-2 text-xs text-ink-subtle">
            <p>
              Regelprijs: <strong>€ {fmtEuro(chosen.priceExcl)}</strong> excl. btw. De bedragen op de
              aanvraag worden hierop aangepast.
            </p>
            {isGoedgekeurd ? (
              <p className="mt-1">
                De aanvraag is al goedgekeurd en komt direct op <strong>te factureren</strong> voor deze partner.
              </p>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
            {error}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !selected}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Bezig..." : "Koppelen"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ApplicationPartnerModal;
